/**
 * AI contract assistant — chat drawer (ADR-0076).
 *
 * Streams one assistant turn at a time as NDJSON frames. Phase 1 tools
 * (recommend a template, explain fields) run silently. Phase 2 write tools
 * (create a draft, fill fields) are **confirmation-gated**: when the model
 * proposes one, the turn pauses on a `confirm_required` frame and we render a
 * confirm card; on Approve/Deny we resend the conversation plus an `approvals`
 * list and the server executes the approved write (ADR-0076).
 *
 * History model: `committed` holds only clean string turns (user + final
 * assistant) — that's what we render and what seeds the next exchange. The
 * paused assistant turn (text + `tool_use` blocks) lives in `workingRef` only
 * for the resume request, so the committed history never carries a dangling
 * `tool_use`.
 */

import { useEffect, useRef, useState } from 'react';
import {
  tenantApi,
  ApiError,
  type ChatFrame,
  type ChatTurnMessage,
  type ChatApproval,
  type ChatContentBlock,
} from '../api.js';

type PendingConfirm = { id: string; name: string; input: unknown; summary: string };

const TOOL_LABELS: Record<string, string> = {
  list_templates: 'Letar i mallbiblioteket…',
  describe_template_fields: 'Läser mallens fält…',
  create_contract: 'Skapar avtal…',
  update_contract_draft: 'Uppdaterar utkast…',
};

export function ChatPanel({ onClose }: { onClose: () => void }) {
  const [committed, setCommitted] = useState<ChatTurnMessage[]>([]);
  const [input, setInput] = useState('');
  const [live, setLive] = useState(''); // in-progress / paused assistant text
  const [activity, setActivity] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingConfirm[]>([]);
  const [decisions, setDecisions] = useState<Record<string, ChatApproval>>({});

  const abortRef = useRef<AbortController | null>(null);
  const workingRef = useRef<ChatTurnMessage[]>([]); // history to resend on resume
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [committed, live, activity, pending]);

  // Run one server exchange (initial send, or a resume carrying approvals).
  async function runExchange(working: ChatTurnMessage[], approvals?: ChatApproval[]) {
    setStreaming(true);
    setError(null);
    setActivity(null);
    setLive('');
    setPending([]);
    setDecisions({});

    const ac = new AbortController();
    abortRef.current = ac;
    let acc = '';
    const confirms: PendingConfirm[] = [];

    const onFrame = (f: ChatFrame) => {
      switch (f.type) {
        case 'text':
          acc += f.text;
          setLive(acc);
          break;
        case 'tool_call':
          setActivity(TOOL_LABELS[f.name] ?? 'Arbetar…');
          break;
        case 'tool_result':
          setActivity(null);
          break;
        case 'confirm_required':
          confirms.push({ id: f.id, name: f.name, input: f.input, summary: f.summary });
          break;
        case 'error':
          setError(
            f.code === 'assistant_unavailable'
              ? 'Assistenten är inte tillgänglig just nu.'
              : 'Något gick fel. Försök igen.',
          );
          break;
        case 'done':
          break;
      }
    };

    try {
      await tenantApi.chat.streamTurn(
        approvals ? { messages: working, approvals } : { messages: working },
        onFrame,
        { signal: ac.signal },
      );

      if (confirms.length > 0) {
        // Pause: stash the assistant tool_use turn (text + tool_use blocks) for
        // the resume request; keep `live` showing the pre-confirm text and
        // render the confirm card(s). Nothing is committed until it resolves.
        const blocks: ChatContentBlock[] = [
          ...(acc ? [{ type: 'text' as const, text: acc }] : []),
          ...confirms.map((c) => ({
            type: 'tool_use' as const,
            id: c.id,
            name: c.name,
            input: c.input,
          })),
        ];
        workingRef.current = [...working, { role: 'assistant', content: blocks }];
        setPending(confirms);
      } else if (acc) {
        setCommitted((prev) => [...prev, { role: 'assistant', content: acc }]);
        setLive('');
      }
    } catch (err) {
      if (!ac.signal.aborted) {
        setError(err instanceof ApiError ? 'Kunde inte nå assistenten.' : 'Oväntat fel.');
      }
    } finally {
      setStreaming(false);
      setActivity(null);
    }
  }

  function send() {
    const text = input.trim();
    if (!text || streaming || pending.length > 0) return;
    const working: ChatTurnMessage[] = [...committed, { role: 'user', content: text }];
    setCommitted(working);
    setInput('');
    void runExchange(working);
  }

  // Record a confirm decision; when every pending write has one, resume.
  function decide(id: string, decision: 'allow' | 'deny') {
    if (streaming) return;
    const next: Record<string, ChatApproval> = {
      ...decisions,
      [id]: { tool_use_id: id, decision },
    };
    setDecisions(next);
    if (pending.every((p) => next[p.id])) {
      void runExchange(
        workingRef.current,
        pending.map((p) => next[p.id]!),
      );
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Avtalsassistent"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-md flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-stone-200 p-4">
          <div>
            <h2 className="text-sm font-semibold text-stone-900">Avtalsassistent</h2>
            <p className="text-xs text-stone-500">Hjälper dig hitta rätt mall</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Stäng"
            className="rounded p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
          >
            ✕
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
          {committed.length === 0 && !live && (
            <p className="rounded border border-dashed border-stone-300 p-4 text-center text-xs text-stone-500">
              Beskriv vad du vill skapa — t.ex. ”ett tidsbestämt bostadshyresavtal” —
              så föreslår jag en mall och vilka uppgifter som behövs.
            </p>
          )}

          {committed.map((m, i) =>
            typeof m.content === 'string' ? (
              <Bubble key={i} role={m.role} text={m.content} />
            ) : null,
          )}

          {live && <Bubble role="assistant" text={live} streaming={streaming} />}

          {activity && <p className="px-1 text-xs italic text-stone-400">{activity}</p>}

          {pending.map((p) => (
            <ConfirmCard
              key={p.id}
              summary={p.summary}
              decided={decisions[p.id]?.decision ?? null}
              onAllow={() => decide(p.id, 'allow')}
              onDeny={() => decide(p.id, 'deny')}
            />
          ))}

          {error && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
              {error}
            </div>
          )}
        </div>

        <form
          className="flex items-end gap-2 border-t border-stone-200 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={2}
            placeholder={pending.length > 0 ? 'Bekräfta åtgärden ovan…' : 'Skriv ett meddelande…'}
            disabled={streaming || pending.length > 0}
            className="flex-1 resize-none rounded border border-stone-300 p-2 text-sm focus:border-stone-500 focus:outline-none disabled:bg-stone-50"
          />
          <button
            type="submit"
            disabled={streaming || pending.length > 0 || !input.trim()}
            className="rounded bg-stone-900 px-3 py-2 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-40"
          >
            {streaming ? '…' : 'Skicka'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ConfirmCard({
  summary,
  decided,
  onAllow,
  onDeny,
}: {
  summary: string;
  decided: 'allow' | 'deny' | null;
  onAllow: () => void;
  onDeny: () => void;
}) {
  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm">
      <p className="mb-2 text-stone-800">
        <span className="font-medium">Bekräfta:</span> {summary}
      </p>
      {decided ? (
        <p className="text-xs text-stone-500">
          {decided === 'allow' ? '✓ Godkänt' : '✕ Avvisat'}
        </p>
      ) : (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onAllow}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800"
          >
            Godkänn
          </button>
          <button
            type="button"
            onClick={onDeny}
            className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            Avbryt
          </button>
        </div>
      )}
    </div>
  );
}

function Bubble({
  role,
  text,
  streaming,
}: {
  role: 'user' | 'assistant';
  text: string;
  streaming?: boolean;
}) {
  const isUser = role === 'user';
  return (
    <div className={isUser ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={
          isUser
            ? 'max-w-[85%] whitespace-pre-wrap rounded-lg bg-stone-900 px-3 py-2 text-sm text-white'
            : 'max-w-[85%] whitespace-pre-wrap rounded-lg bg-stone-100 px-3 py-2 text-sm text-stone-800'
        }
      >
        {text}
        {streaming && <span className="ml-0.5 animate-pulse">▍</span>}
      </div>
    </div>
  );
}
